import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { CIBLE_GESTE, type Geste } from "./contract";
import { GITHUB_BLOB } from "./markdown";
import {
  heureDeDemande,
  oublierDemande,
  RELECTURE_DEMANDE_MS,
  toujoursRien,
  useDemandes,
  useTicTac,
} from "./portefeuille-demandes";
import { statutDe, useGesteFait } from "./queries";

/**
 * Le bouton « C'est fait » de la carte « Cocher » (spec des gestes guides, section 5 ;
 * maquette `Bouton.dc.html`). C'est la premiere ecriture qui part d'une page du portefeuille,
 * et elle est etroite : une seule action, une seule entree (la cible), et c'est un workflow
 * qui commite. La page ne coche rien elle-meme.
 *
 * Ce qui explique la forme du fichier :
 *
 * - **Deux temps, sans fenetre modale.** « C'est fait » arme, « Confirmer » envoie ; sans
 *   reponse, la confirmation retombe seule au repos. Un clic distrait ne part pas.
 * - **Le 202 n'est pas le geste coche.** Il dit que le workflow est parti ; le commit arrive
 *   une a deux minutes plus tard. La demande est gardee dans le navigateur
 *   (`portefeuille-demandes.ts`) et la page se relit jusqu'a voir `fait` : rien ici ne dit
 *   qu'un geste est fait tant que la lecture du depot ne le dit pas.
 * - **Un seul etat a la fois, derive de ce qu'on sait** (`etatDuBouton`) plutot que tenu a
 *   la main : le geste lu, la demande gardee, la requete en cours ou son refus, la
 *   confirmation armee. Un etat tenu a la main finirait par contredire la lecture.
 * - **Une seule commande de secours par carte.** Le bloc « Cocher » du guide, juste sous le
 *   bouton, la porte deja (la CI l'exige) : quand rien ne part, le bouton y renvoie en une
 *   ligne. Il ne donne la sienne que si le guide ne l'a pas, servi par un serveur en retard.
 * - **Pas de `pre` ici.** La page du guide enveloppe chaque `pre` de son conteneur pour lui
 *   poser un bouton Copier : elle deplacerait des noeuds que React croit tenir. La commande
 *   de secours est un `code`, avec son propre bouton.
 */

/** Sans reponse, la confirmation retombe au repos : un clic oublie ne reste pas arme. */
export const CONFIRMATION_MS = 6000;

/** Le bouton Copier revient au repos au bout de 4 s, comme ceux du guide. */
export const COPIE_MS = 4000;

/** Ce qu'il faut ouvrir quand rien ne vient : le runner du PC est le suspect habituel. */
export const PLAYBOOK_RUNNER = `${GITHUB_BLOB}docs/playbooks/runner-auto-heberge.md`;

export type EtatDuBouton =
  | "repos"
  | "confirmation"
  | "envoi"
  | "attente"
  | "toujours-rien"
  | "refuse"
  | "deja-fait"
  | "fait";

/**
 * L'etat affiche, dans l'ordre de ce qui fait foi : la lecture du depot d'abord (fait), puis
 * le refus qui dit que la page est en retard (409), puis la demande gardee, puis la requete
 * en cours ou refusee, et enfin le geste de Nicolas (confirmation armee ou non).
 */
export function etatDuBouton(entree: {
  fait: boolean;
  demandeLe: string | null;
  maintenant: number;
  envoi: { isPending: boolean; isError: boolean; error: unknown };
  arme: boolean;
}): EtatDuBouton {
  const { fait, demandeLe, maintenant, envoi, arme } = entree;
  if (fait) return "fait";
  if (envoi.isError && statutDe(envoi.error) === 409) return "deja-fait";
  if (demandeLe !== null) return toujoursRien(demandeLe, maintenant) ? "toujours-rien" : "attente";
  if (envoi.isPending) return "envoi";
  if (envoi.isError) return "refuse";
  return arme ? "confirmation" : "repos";
}

/** Ce qui peut recevoir le focus : trois boutons, et la phrase d'etat. */
export type Focus = "confirmer" | "fait" | "reessayer" | "statut";

/**
 * Ou poser le focus apres un changement d'etat, ou `null` pour le laisser ou il est. Le bouton
 * qui avait le focus disparait presque a chaque fois : sans cible, le focus du clavier
 * retomberait sur `body`. Les changements que personne n'a provoques (le commit qui arrive,
 * les cinq minutes qui passent) ne le deplacent pas.
 */
export function focusApres(avant: EtatDuBouton, apres: EtatDuBouton): Focus | null {
  if (apres === "confirmation") return "confirmer";
  if (apres === "repos") {
    return avant === "confirmation" || avant === "envoi" || avant === "refuse" ? "fait" : null;
  }
  // Nicolas vient de confirmer. Une reponse rapide arrive avant meme que l'envoi ne soit
  // affiche : la confirmation passe alors directement a l'attente ou au refus.
  if ((avant !== "confirmation" && avant !== "envoi") || apres === "envoi") return null;
  return apres === "refuse" ? "reessayer" : "statut";
}

/**
 * Vrai quand le focus est perdu (sur `body`, ou sur un bouton qui vient de sortir du document)
 * ou encore dans le bouton. Faux quand Nicolas l'a pose ailleurs dans la page entre-temps :
 * une confirmation qui retombe au repos ne doit pas le lui reprendre.
 */
function focusAReprendre(zone: HTMLElement | null): boolean {
  const actif = document.activeElement;
  return (
    actif === null ||
    actif === document.body ||
    !actif.isConnected ||
    zone?.contains(actif) === true
  );
}

/** Les notes sous un etat : plus petites que le texte courant, comme dans la maquette. */
const NOTE = "text-[0.8125rem] leading-normal text-muted-foreground";

export function BoutonCocher({
  geste,
  secoursDansLeGuide,
}: {
  geste: Geste;
  /** Vrai quand le bloc du guide, juste sous le bouton, porte deja la commande de secours. */
  secoursDansLeGuide: boolean;
}) {
  // Une cible qui n'a pas la forme attendue n'irait nulle part : le backend la refuserait, et
  // la commande de secours la ferait coller dans un terminal. Le bouton ne s'affiche pas, et le
  // bloc « Cocher » du guide garde sa propre commande.
  const cible = geste.cible !== null && CIBLE_GESTE.test(geste.cible) ? geste.cible : null;
  const maintenant = Date.now();
  const demandes = useDemandes(maintenant);
  const envoi = useGesteFait();
  const [arme, setArme] = useState(false);
  const zone = useRef<HTMLDivElement>(null);
  const statut = useRef<HTMLDivElement>(null);
  // Un verrou synchrone : un double clic arrive avant que `isPending` ne soit rendu, et
  // lancerait deux fois le workflow.
  const enVol = useRef(false);

  const demandeLe = geste.fait || cible === null ? null : (demandes[cible] ?? null);
  const etat = etatDuBouton({ fait: geste.fait, demandeLe, maintenant, envoi, arme });

  // L'horloge ne tourne que pendant l'attente : c'est elle qui fait passer a « toujours rien »
  // au bout de cinq minutes, et rendre le bouton au bout d'une heure.
  useTicTac(RELECTURE_DEMANDE_MS, demandeLe !== null);

  useEffect(() => {
    if (etat !== "confirmation") return;
    const minuterie = setTimeout(() => setArme(false), CONFIRMATION_MS);
    return () => clearTimeout(minuterie);
  }, [etat]);

  // Le geste est fait : la demande gardee n'a plus rien a dire.
  useEffect(() => {
    if (geste.fait && cible !== null) oublierDemande(cible);
  }, [geste.fait, cible]);

  const precedent = useRef(etat);
  useEffect(() => {
    const avant = precedent.current;
    precedent.current = etat;
    if (avant === etat) return;
    const vers = focusApres(avant, etat);
    if (vers === null) return;
    if (vers !== "confirmer" && !focusAReprendre(zone.current)) return;
    if (vers === "statut") statut.current?.focus();
    else zone.current?.querySelector<HTMLElement>(`[data-cocher="${vers}"]`)?.focus();
  }, [etat]);

  if (cible === null && !geste.fait) return null;

  const desarmer = (): void => setArme(false);
  const surEchap = (evenement: KeyboardEvent<HTMLButtonElement>): void => {
    if (evenement.key !== "Escape") return;
    evenement.preventDefault();
    desarmer();
  };
  const confirmer = (): void => {
    if (cible === null || enVol.current) return;
    enVol.current = true;
    envoi.mutate(
      { cible },
      {
        // Desarmer a la reponse, pas au clic : l'etat affiche pendant l'envoi ne depend alors
        // pas de l'ordre dans lequel React et la requete se rendent la main.
        onSettled: () => {
          enVol.current = false;
          setArme(false);
        },
      },
    );
  };
  const message =
    envoi.error instanceof Error && envoi.error.message !== ""
      ? envoi.error.message
      : "La demande n'a pas abouti.";

  return (
    <div className="flex flex-col gap-3" ref={zone}>
      {/* Tout ce qui change d'un etat a l'autre est lu a voix haute ; les boutons, eux, sont
          hors de la region, pour ne pas etre annonces a chaque changement. */}
      <div
        aria-live="polite"
        className="flex flex-col gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        ref={statut}
        tabIndex={-1}
      >
        {etat === "repos" ? <p>Quand « Vérifier » est bon :</p> : null}
        {etat === "confirmation" || etat === "envoi" ? <p>Marquer ce geste comme fait ?</p> : null}
        {etat === "attente" && demandeLe !== null ? <Attente demandeLe={demandeLe} /> : null}
        {etat === "toujours-rien" && demandeLe !== null ? (
          <ToujoursRien demandeLe={demandeLe} />
        ) : null}
        {etat === "refuse" ? (
          <div className="flex flex-col gap-1 rounded-md border border-destructive/40 bg-destructive/[0.06] px-3 py-2.5 text-destructive">
            <p className="font-semibold">Le workflow n'est pas parti.</p>
            <p>{message}</p>
          </div>
        ) : null}
        {etat === "deja-fait" ? (
          <p className="rounded-md border border-border bg-muted px-3 py-2.5">
            Déjà coché : la page se met à jour.
          </p>
        ) : null}
        {etat === "fait" ? <Fait /> : null}
      </div>

      {etat === "repos" ? (
        <>
          <Button
            className="h-11 w-full font-semibold"
            data-cocher="fait"
            onClick={() => setArme(true)}
          >
            C'est fait
          </Button>
          <p className={NOTE}>
            Le geste disparaît de « Ce qui t'attend » en une à deux minutes : un workflow commite
            pour toi.
          </p>
        </>
      ) : null}
      {etat === "confirmation" || etat === "envoi" ? (
        <div className="flex items-center gap-2">
          <Button
            className="h-11 flex-1 font-semibold"
            data-cocher="confirmer"
            disabled={etat === "envoi"}
            onClick={confirmer}
            onKeyDown={surEchap}
          >
            {etat === "envoi" ? "Envoi…" : "Confirmer"}
          </Button>
          <Button
            className="h-11"
            disabled={etat === "envoi"}
            onClick={desarmer}
            onKeyDown={surEchap}
            variant="outline"
          >
            Annuler
          </Button>
        </div>
      ) : null}
      {etat === "refuse" ? (
        <Button
          className="self-start"
          data-cocher="reessayer"
          onClick={() => envoi.reset()}
          variant="outline"
        >
          Réessayer
        </Button>
      ) : null}
      {(etat === "toujours-rien" || etat === "refuse") && cible !== null ? (
        secoursDansLeGuide ? (
          <p className={NOTE}>
            En secours : la commande du guide, juste en dessous, depuis PowerShell sur le PC.
          </p>
        ) : (
          <Secours cible={cible} />
        )
      ) : null}
    </div>
  );
}

function Attente({ demandeLe }: { demandeLe: string }) {
  return (
    <>
      <p className="flex items-center gap-2.5 rounded-md bg-etape-test/15 px-3 py-2.5 font-semibold text-etape-test">
        <span
          aria-hidden="true"
          className="size-2.5 shrink-0 rounded-full bg-etape-test motion-safe:animate-pulse"
        />
        Demande envoyée à {heureDeDemande(demandeLe)}
      </p>
      <p className={NOTE}>
        Le geste disparaîtra d'ici une à deux minutes. Tu peux quitter la page : l'état est gardé
        dans ce navigateur, la vérité reste le dépôt.
      </p>
    </>
  );
}

function ToujoursRien({ demandeLe }: { demandeLe: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-avertissement/60 bg-avertissement/15 px-3 py-2.5">
      <p className="font-semibold">
        Toujours rien ? Demande envoyée à {heureDeDemande(demandeLe)}.
      </p>
      <p>
        Le runner du PC est peut-être éteint :{" "}
        <a
          className="underline underline-offset-2"
          href={PLAYBOOK_RUNNER}
          rel="noopener noreferrer"
          target="_blank"
        >
          playbook du runner
        </a>
        .
      </p>
    </div>
  );
}

function Fait() {
  return (
    <>
      <p className="flex items-center gap-2.5 rounded-md bg-etape-live/15 px-3 py-2.5 font-semibold text-etape-live">
        <span
          aria-hidden="true"
          className="flex size-4 shrink-0 items-center justify-center rounded bg-etape-live text-xs text-card"
        >
          ✓
        </span>
        Fait
      </p>
      <p className={NOTE}>
        Le guide reste lisible pour la trace. Décocher ne se fait pas d'ici : c'est une session
        Claude ou une édition du fichier.
      </p>
    </>
  );
}

/**
 * La commande de secours, quand le bouton n'a pas suffi : le meme geste depuis PowerShell,
 * sur le PC, sans passer par la console ni par lab-tools.
 */
function Secours({ cible }: { cible: string }) {
  const commande = `./scripts/geste-fait.ps1 ${cible}`;
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        En secours, depuis PowerShell
      </p>
      <div className="flex flex-col gap-2 rounded-md border border-border bg-muted p-3">
        {/* Une commande revient a la ligne plutot que de defiler : c'est la cible, en fin de
            ligne, qu'il faut lire avant de copier. */}
        <code className="font-mono text-[0.8125rem] leading-relaxed wrap-anywhere">{commande}</code>
        <CopierCommande commande={commande} />
      </div>
    </div>
  );
}

type EtatCopie = "Copier" | "Copié" | "Copie impossible";

function CopierCommande({ commande }: { commande: string }) {
  const [etat, setEtat] = useState<EtatCopie>("Copier");
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (minuterie.current !== null) clearTimeout(minuterie.current);
    },
    [],
  );

  const dire = (texte: EtatCopie): void => {
    setEtat(texte);
    if (minuterie.current !== null) clearTimeout(minuterie.current);
    // Le bouton revient au repos : un « Copie impossible » fige laisserait croire qu'il ne
    // marchera plus jamais, alors qu'une permission peut etre accordee entre-temps.
    minuterie.current = setTimeout(() => setEtat("Copier"), COPIE_MS);
  };

  const copier = (): void => {
    // Le presse-papiers n'existe pas partout (contexte non securise, refus) : le bouton le dit
    // plutot que de faire semblant.
    const promesse = navigator.clipboard?.writeText(commande);
    if (promesse === undefined) {
      dire("Copie impossible");
      return;
    }
    promesse.then(
      () => dire("Copié"),
      () => dire("Copie impossible"),
    );
  };

  return (
    // Le bouton s'annonce lui-meme, comme ceux du guide : son nom change quand la copie
    // aboutit ou echoue.
    <Button
      aria-label={`${etat} : commande de secours`}
      aria-live="polite"
      className="self-end"
      onClick={copier}
      size="sm"
      variant="outline"
    >
      {etat}
    </Button>
  );
}
